// src/api/client.js
import axios from "axios";
import { getUserFriendlyError } from "../utils/errorMessages.js";
import { getPortal, isAuthenticated } from "../utils/auth.js";
import { notifySessionExpiryUI } from "../utils/sessionExpiryUI.js";
import { extractCredentials } from "../utils/loginAuth.js";

/**
 * ENV handling (CRA & Vite)
 */
const ENV = typeof import.meta !== "undefined" ? import.meta.env : process.env;
const isDevelopment = ENV?.NODE_ENV === 'development' || ENV?.MODE === 'development';

// Set to empty string for relative URLs via proxy
const BASE = "";

const AUTH_ENDPOINT_PATTERNS = [
  "/api/method/frappe.core.doctype.user.user.sign_up",
  "/api/method/dynesourcerapi.api.custom_signup",
  "/api/method/dynesourcerapi.api.verify_and_set_password",
  "/api/method/dynesourcerapi.password_reset.request_password_reset",
  "/api/method/frappe.core.doctype.user.user.update_password",
  "/api/method/login",
  "dynesourcerapi.author.login",
  "dynesourcerapi.author.login_verify_otp",
  "/api/method/logout",
];

/** Build full URL respecting BASE if provided */
function buildUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return BASE ? `${BASE}${p}` : p;
}

function isAuthEndpointUrl(url = "") {
  return AUTH_ENDPOINT_PATTERNS.some((pattern) => String(url).includes(pattern));
}

/**
 * Public marketing / sourcing pages — no login required.
 * A 401 here must not open the "Session expired" modal.
 */
function isPublicSourcingPath(path = "") {
  const p = String(path || "");
  if (p.startsWith("/sourcing")) return true;
  if (p.startsWith("/invoice-finance")) return true;
  if (p.startsWith("/mortgages/invoice-finance")) return true;
  if (p.startsWith("/mortgages/buy-to-let")) return true;
  return false;
}

// Auth uses API key + secret (Authorization header) together with Frappe sid cookies.

/* =========================
   AXIOS INSTANCE
   ========================= */

const api = axios.create({
  withCredentials: true, // CRITICAL: This ensures cookies are sent
  headers: { Accept: "application/json" },
  timeout: 30000,
});

let expectedSessionUser = null;

/** Set CSRF token from Frappe session */
export function setCsrfToken(token) {
  const isDevelopment = ENV?.NODE_ENV === 'development' || ENV?.MODE === 'development';
  if (token) {
    api.defaults.headers.common["X-Frappe-CSRF-Token"] = token;
    // persist across reloads
    sessionStorage.setItem("CSRF_TOKEN", token);
    if (isDevelopment) {
      console.log("🔐 CSRF token set");
    }
  } else {
    delete api.defaults.headers.common["X-Frappe-CSRF-Token"];
    sessionStorage.removeItem("CSRF_TOKEN");
    if (isDevelopment) {
      console.log("🔐 CSRF token cleared");
    }
  }
}

const FRAPPE_SESSION_COOKIES = ["sid", "system_user", "user_id", "full_name", "user_image"];

/** Remove Frappe session cookies from the browser (including sid=Guest). */
export function clearSessionCookies() {
  if (typeof document === "undefined") return;
  const paths = ["/", "/api"];
  for (const name of FRAPPE_SESSION_COOKIES) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    for (const path of paths) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
    }
  }
}

/** True when a non-Guest sid cookie is present (sid is HttpOnly — often unreadable). */
export function hasRealSessionCookie() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((part) => {
    const [name, value] = part.trim().split("=");
    return name === "sid" && value && value !== "Guest";
  });
}

/**
 * Frappe mirrors session identity in readable cookies (user_id, full_name).
 * sid is HttpOnly so we use user_id !== Guest as the reliable browser signal.
 */
export function hasAuthenticatedCookieSession() {
  if (typeof document === "undefined") return false;
  for (const part of document.cookie.split(";")) {
    const [name, raw] = part.trim().split("=");
    if (name === "user_id") {
      const userId = decodeURIComponent(raw || "").trim();
      return Boolean(userId && userId.toLowerCase() !== "guest");
    }
  }
  return false;
}

function normalizeSessionEmail(value) {
  return String(value ?? "").trim().toLowerCase();
}

/** Read the user_id cookie Frappe mirrors for the active browser session. */
export function readCookieUserId() {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [name, raw] = part.trim().split("=");
    if (name === "user_id") {
      const userId = normalizeSessionEmail(decodeURIComponent(raw || ""));
      return userId && userId !== "guest" ? userId : null;
    }
  }
  return null;
}

function shouldPreferCookieSession(url = "") {
  if (!String(url).includes("/api/resource/")) return false;
  if (!hasAuthenticatedCookieSession()) return false;

  const expected = getExpectedSessionUser();
  if (!expected) return true;

  const cookieUser = readCookieUserId();
  if (!cookieUser) return true;

  return cookieUser === normalizeSessionEmail(expected);
}

/** Clear Guest sid if it would shadow a real login session. */
export function clearGuestSessionCookie() {
  if (typeof document === "undefined") return;
  if (document.cookie.includes("sid=Guest")) {
    document.cookie = "sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/api;";
  }
}

/** Invalidate the server session; safe to call when already logged out. */
export async function logoutBackend() {
  try {
    await api.post("/api/method/logout", null, { _skipSessionExpiry: true });
  } catch {
    // Expected when there is no active session.
  }
}

/**
 * Tear down any existing portal session before a new sign-in.
 * Invalidates the server session first so stale admin/borrower cookies cannot
 * shadow the next login. A brief Guest sid before the new login is expected.
 */
export async function prepareForLogin() {
  try {
    await logoutBackend();
  } catch {
    // Continue with local cleanup when no active server session exists.
  }
  clearSessionCookies();
  clearGuestSessionCookie();
  clearAuth();
}

/** Full sign-out: backend logout, storage, and cookies. */
export async function signOut() {
  try {
    await logoutBackend();
  } catch {
    // Continue with local cleanup.
  }
  try {
    // Prevent Google One Tap / silent re-auth after logout (GIS).
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch {
    // ignore
  }
  clearAuth();
  clearSessionCookies();
}

/** Set API credentials for token auth */
export function setApiCredentials(apiKey, apiSecret, remember = false) {
  if (!apiKey || !apiSecret) return;
  const tokenValue = `token ${apiKey}:${apiSecret}`;
  applyAuthorizationHeader(api.defaults.headers, tokenValue);
  api.defaults.headers.common.Authorization = tokenValue;
  const storage = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  storage.setItem("API_KEY", apiKey);
  storage.setItem("API_SECRET", apiSecret);
  other.removeItem("API_KEY");
  other.removeItem("API_SECRET");
}

function applyAuthorizationHeader(headers, tokenValue) {
  if (!headers) return;
  headers.Authorization = tokenValue;
  if (typeof headers.set === "function") {
    headers.set("Authorization", tokenValue);
  }
  if (headers.common) {
    headers.common.Authorization = tokenValue;
  }
}

function clearAuthorizationHeader(headers) {
  if (!headers) return;
  delete headers.Authorization;
  if (typeof headers.delete === "function") {
    headers.delete("Authorization");
  }
  if (headers.common) {
    delete headers.common.Authorization;
  }
}

function readStoredApiCredentials() {
  const apiKey =
    sessionStorage.getItem("API_KEY") || localStorage.getItem("API_KEY");
  const apiSecret =
    sessionStorage.getItem("API_SECRET") || localStorage.getItem("API_SECRET");
  if (!apiKey || !apiSecret) return null;
  return `token ${apiKey}:${apiSecret}`;
}

/** True when API key + secret are persisted for token auth. */
export function hasStoredApiCredentials() {
  return Boolean(readStoredApiCredentials());
}

/**
 * Native Frappe login sets HttpOnly session cookies (sid). Custom author.login
 * returns API keys but may not set browser cookies — call both on direct login.
 */
export async function establishFrappeCookieSession({ email, password }) {
  const formData = new URLSearchParams();
  formData.append("usr", email);
  formData.append("pwd", password);
  const res = await api.post("/api/method/login", formData, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return { data: res.data, headers: res.headers };
}

function parseLoggedInUserEmail(payload) {
  const userData = payload?.message ?? payload;
  if (typeof userData === "string") {
    return normalizeSessionEmail(userData) || null;
  }
  if (userData && typeof userData === "object") {
    const email = userData.email || userData.name || userData.user || "";
    return normalizeSessionEmail(email) || null;
  }
  return null;
}

/** Resolve the email Frappe associates with the current API session. */
export async function fetchLoggedInUserEmail() {
  try {
    const res = await api.get("/api/method/frappe.auth.get_logged_user", {
      _skipSessionExpiry: true,
    });
    return parseLoggedInUserEmail(res?.data);
  } catch {
    return null;
  }
}

/**
 * After password login, confirm cookie session matches the signed-in user.
 * Skipped for OTP-only completion (no password / no cookie bootstrap).
 */
async function verifyCookieSessionMatchesUser(expectedEmail) {
  const expected = normalizeSessionEmail(expectedEmail);
  if (!expected) {
    throw new Error("SESSION_VERIFY_FAILED");
  }
  if (!hasAuthenticatedCookieSession()) {
    return;
  }

  const cookieUser = readCookieUserId();
  if (!cookieUser) {
    return;
  }
  if (cookieUser !== expected) {
    throw new Error("SESSION_MISMATCH");
  }
}

/**
 * Store tokens/CSRF from custom login, then establish cookie session when password
 * is available (direct login). OTP completion passes no password.
 */
export async function finalizeLoginSession({
  email,
  password,
  loginResponse,
  loginPayload,
  remember,
  requireCredentials = true,
}) {
  const responseHeaders = loginResponse?.headers || {};
  setSessionFromLoginResponse(
    loginResponse?.data || loginPayload,
    responseHeaders,
    remember
  );

  if (password) {
    try {
      const frappeSession = await establishFrappeCookieSession({ email, password });
      setSessionFromLoginResponse(
        frappeSession.data,
        frappeSession.headers,
        remember
      );
      await verifyCookieSessionMatchesUser(email);
    } catch (err) {
      if (
        err?.message === "SESSION_MISMATCH" ||
        err?.message === "SESSION_VERIFY_FAILED"
      ) {
        throw err;
      }
      if (isDevelopment) {
        console.warn("Frappe cookie session setup failed:", err?.message);
      }
    }
  }

  if (requireCredentials && !hasStoredApiCredentials()) {
    throw new Error("LOGIN_CREDENTIALS_MISSING");
  }
}

/** No-op: API token storage removed for security */
export function setAuthToken(tokenInput) {
  if (isDevelopment) {
    console.log("ℹ️ setAuthToken called but disabled for security. Using cookies.");
  }
}

/** No-op: API token storage removed for security */
export function setAuthFromPostmanHeaderLine(line) {
  if (isDevelopment) {
    console.log("ℹ️ setAuthFromPostmanHeaderLine called but disabled for security.");
  }
}

/**
 * IMPORTANT: Call this after successful login to store session data
 */
export function setSessionFromLoginResponse(response, responseHeaders = null, remember = false) {
  if (isDevelopment) {
    console.log("📝 Storing session from login response");
  }

  const { apiKey, apiSecret, user: credUser } = extractCredentials(response);
  if (apiKey && apiSecret) {
    setApiCredentials(apiKey, apiSecret, remember);
    if (isDevelopment) {
      console.info("✅ API token auth enabled from login response");
    }
  } else if (isDevelopment) {
    console.warn("⚠️ Login response missing api_key/api_secret", response);
  }

  // Store CSRF token - check multiple sources
  let csrfToken = null;

  // 1. From response body
  if (response.csrf_token) {
    csrfToken = response.csrf_token;
  }

  // 2. From response headers
  if (!csrfToken && responseHeaders) {
    csrfToken = responseHeaders['x-frappe-csrf-token'] ||
      responseHeaders['X-Frappe-CSRF-Token'] ||
      responseHeaders['csrf-token'] ||
      responseHeaders['csrf_token'];
  }

  // 3. From nested message/data
  if (!csrfToken) {
    const message = response.message || response.data || {};
    if (message.csrf_token) {
      csrfToken = message.csrf_token;
    }
  }

  if (csrfToken) {
    setCsrfToken(csrfToken);
    if (isDevelopment) {
      console.log("✅ CSRF token stored from login");
    }
  } else {
    if (isDevelopment) {
      console.warn("⚠️ No CSRF token found in login response");
    }
  }

  // Store user info
  const responseUser =
    credUser || response?.user || response?.message?.user || null;
  if (responseUser) {
    expectedSessionUser = String(responseUser).trim().toLowerCase();
    sessionStorage.setItem("user", responseUser);
    localStorage.setItem("user", responseUser);
    sessionStorage.setItem("EXPECTED_SESSION_USER", expectedSessionUser);
    localStorage.setItem("EXPECTED_SESSION_USER", expectedSessionUser);
  }
}

export function getExpectedSessionUser() {
  return (
    expectedSessionUser ||
    sessionStorage.getItem("EXPECTED_SESSION_USER") ||
    localStorage.getItem("EXPECTED_SESSION_USER") ||
    null
  );
}

/**
 * Clear all auth data (for logout)
 * Comprehensive cleanup of all authentication-related storage
 */
export function clearAuth() {
  if (isDevelopment) {
    console.log("🧹 Clearing all session and auth data");
  }
  
  // Clear auth tokens
  localStorage.removeItem("AUTH_TOKEN");
  sessionStorage.removeItem("AUTH_TOKEN");
  localStorage.removeItem("API_KEY");
  localStorage.removeItem("API_SECRET");
  sessionStorage.removeItem("API_KEY");
  sessionStorage.removeItem("API_SECRET");
  clearAuthorizationHeader(api.defaults.headers);
  delete api.defaults.headers.common.Authorization;
  
  sessionStorage.removeItem("user");
  localStorage.removeItem("user");
  sessionStorage.removeItem("EXPECTED_SESSION_USER");
  localStorage.removeItem("EXPECTED_SESSION_USER");
  expectedSessionUser = null;
  sessionStorage.removeItem("CSRF_TOKEN");
  localStorage.removeItem("CSRF_TOKEN");
  setCsrfToken(null);
  
  // Clear auth state flags
  localStorage.removeItem("ds_isAuthed");
  sessionStorage.removeItem("ds_isAuthed");
  localStorage.removeItem("ds_portal");
  sessionStorage.removeItem("ds_portal");
  localStorage.removeItem("ds_lender_email");
  sessionStorage.removeItem("ds_lender_email");
  
  // Clear borrower-specific auth data
  localStorage.removeItem("BORROWER_EMAIL");
  sessionStorage.removeItem("BORROWER_EMAIL");
  localStorage.removeItem("borrowerEmail");
  sessionStorage.removeItem("borrowerEmail");
  localStorage.removeItem("email");
  sessionStorage.removeItem("email");
  localStorage.removeItem("userEmail");
  sessionStorage.removeItem("userEmail");
  
  // Clear any legacy auth keys
  localStorage.removeItem("ds_signup_email");
  sessionStorage.removeItem("ds_signup_email");
}

/** Restore Authorization header from storage on reload */
function bootstrapAuthFromStorage(instance) {
  const tokenValue = readStoredApiCredentials();
  if (tokenValue) {
    applyAuthorizationHeader(instance.defaults.headers, tokenValue);
    instance.defaults.headers.common.Authorization = tokenValue;
    if (isDevelopment) {
      console.log("🔄 Auth credentials restored from storage");
    }
  }
}

/** Restore CSRF token from storage on reload */
function bootstrapCsrfFromStorage(instance) {
  const csrf = sessionStorage.getItem("CSRF_TOKEN") || localStorage.getItem("CSRF_TOKEN");
  if (csrf) {
    instance.defaults.headers.common["X-Frappe-CSRF-Token"] = csrf;
    if (isDevelopment) {
      console.log("🔄 CSRF token restored from storage");
    }
  }
}

// Debug logging (development only, no URL exposure)
if (isDevelopment) {
  console.log("🔧 API Client Configuration:");
  console.log("  Using relative URLs (proxy-based)");
  console.log("  withCredentials:", api.defaults.withCredentials);
}

// 🔁 Bootstrap Auth & CSRF on first import (including page refresh)
bootstrapAuthFromStorage(api);
bootstrapCsrfFromStorage(api);

if (isDevelopment) {
  console.log("ℹ️ Using API token + cookie session authentication.");
}

/* =========================
   INTERCEPTORS
   ========================= */

let inFlightRetry = new WeakSet();

/**
 * Stored token that the server rejected (401) on a guest-capable request.
 * Stale keys (e.g. after a server-side logout) make Frappe 401 even public
 * sourcing calls, so those requests go anonymous while the stale token remains.
 */
let rejectedGuestFallbackToken = null;

// Request interceptor: Remove Authorization header for public endpoints; inject token from storage when needed
api.interceptors.request.use(
  (config) => {
    const url = config.url || "";
    const isAuthEndpoint = isAuthEndpointUrl(url);
    const skipStaleToken =
      config._guestFallback &&
      rejectedGuestFallbackToken &&
      readStoredApiCredentials() === rejectedGuestFallbackToken;

    if (isAuthEndpoint || config._anonymous || skipStaleToken) {
      clearAuthorizationHeader(config.headers);
      if (config.headers.common) delete config.headers.common.Authorization;
    } else {
      const tokenValue = readStoredApiCredentials();
      const preferCookie =
        shouldPreferCookieSession(url) && !config?._forceTokenAuth;

      // Resource APIs: use cookie session when logged in. Sending token + cookie
      // together can make Frappe ignore the session and return 403 for borrowers.
      if (preferCookie) {
        clearAuthorizationHeader(config.headers);
        if (config.headers.common) delete config.headers.common.Authorization;
      } else if (tokenValue) {
        applyAuthorizationHeader(config.headers, tokenValue);
        applyAuthorizationHeader(api.defaults.headers, tokenValue);
        api.defaults.headers.common.Authorization = tokenValue;
      }
    }

    // For PUT/POST/PATCH requests, ensure CSRF token is included
    const method = (config.method || "").toUpperCase();
    if ((method === "PUT" || method === "POST" || method === "PATCH") && !isAuthEndpoint) {
      // Check if CSRF token is already set
      let csrfToken = config.headers["X-Frappe-CSRF-Token"] ||
        api.defaults.headers.common["X-Frappe-CSRF-Token"];

      // If not in headers, try storage
      if (!csrfToken) {
        csrfToken = sessionStorage.getItem("CSRF_TOKEN");
        if (csrfToken) {
          api.defaults.headers.common["X-Frappe-CSRF-Token"] = csrfToken;
        }
      }

      // Always set the token in request headers if we have it
      if (csrfToken) {
        config.headers["X-Frappe-CSRF-Token"] = csrfToken;
      }
    }

    // Debug logging for shortlist API calls (development only, no URL exposure)
    if (isDevelopment && url.includes("shortlist_api.add_shortlisted_product")) {
      console.log("🔍 REQUEST INTERCEPTOR - Shortlist API call:", {
        endpoint: url.split('/api/method/')[1] || '[endpoint]',
        method: config.method,
        hasMortgageProduct: !!config.data?.mortgage_product,
        dataKeys: config.data ? Object.keys(config.data) : []
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (res) => {
    // Extract CSRF token from all successful responses and update it
    // This ensures we always have the latest CSRF token
    const csrfToken = res.headers['x-frappe-csrf-token'] || 
                      res.headers['X-Frappe-CSRF-Token'] ||
                      res.headers['csrf-token'];
    if (csrfToken && csrfToken !== api.defaults.headers.common['X-Frappe-CSRF-Token']) {
      setCsrfToken(csrfToken);
      if (isDevelopment) {
        console.log('🔄 CSRF token updated from response header');
      }
    }
    return res;
  },
  async (err) => {
    const cfg = err?.config;
    if (
      cfg?._guestFallback &&
      !cfg._anonymous &&
      err?.response?.status === 401
    ) {
      const sentToken = readStoredApiCredentials();
      const retryConfig = { ...cfg, _anonymous: true, headers: { ...(cfg.headers || {}) } };
      clearAuthorizationHeader(retryConfig.headers);
      try {
        const res = await api.request(retryConfig);
        if (sentToken) rejectedGuestFallbackToken = sentToken;
        return res;
      } catch (retryErr) {
        return Promise.reject(retryErr);
      }
    }
    if (cfg?._skipSessionExpiry) {
      return Promise.reject(err);
    }
    const status = err?.response?.status;
    const responseData = err?.response?.data;

    const hasAuth = Boolean(readStoredApiCredentials());

    /**
     * Resolve sign-in URL before clearing storage (portal flags still available).
     * Covers borrower routes under /borrower and /mortgages, admin, and lender.
     */
    const resolveSignInBase = (path, portal) => {
      // Public sourcing under /mortgages/* is not the borrower portal
      if (isPublicSourcingPath(path)) {
        return "/borrower/signin";
      }
      if (path.startsWith("/borrower") || portal === "borrower") {
        return "/borrower/signin";
      }
      if (path.startsWith("/admin") || portal === "admin") {
        return "/admin/signin";
      }
      if (path.startsWith("/lender") || portal === "lender") {
        return "/signin";
      }
      return "/signin";
    };

    // Helper: auto-logout on session/cookie expiry and redirect to correct portal sign-in (borrower, lender, or admin)
    const handleSessionExpiry = (reason = "Session expired") => {
      const path = typeof window !== "undefined" ? window.location.pathname || "" : "";

      // Guests and public sourcing pages: never show "Session expired"
      if (!isAuthenticated() || isPublicSourcingPath(path)) {
        if (isDevelopment) {
          console.warn(`🔐 ${reason} ignored on public/guest page (${path || "unknown"}).`);
        }
        return;
      }

      if (typeof window !== "undefined" && window.__dsSessionExpiryHandled) return;
      if (typeof window !== "undefined") {
        window.__dsSessionExpiryHandled = true;
        window.__isSigningOut = true;
      }

      if (isDevelopment) {
        console.warn(`🔐 ${reason}. Signing out and showing session expiry UI...`);
      }

      const search = typeof window !== "undefined" ? window.location.search || "" : "";
      const portal = getPortal() || "";

      const target = resolveSignInBase(path, portal);
      const redirectUrl =
        path && !path.includes("signin") && !path.includes("signup")
          ? `${target}?expired=1&next=${encodeURIComponent(path + search)}`
          : `${target}?expired=1`;

      clearAuth();

      const handled = notifySessionExpiryUI({ reason, redirectUrl, portalLabel: portal });

      if (!handled) {
        window.location.href = redirectUrl;
      }
    };

    // 1) Session Expiry (401 Unauthorized)
    if (status === 401 && !isAuthEndpointUrl(cfg?.url) && !cfg?.url?.includes("/signin")) {
      handleSessionExpiry("Session expired (401 Unauthorized)");
      return Promise.reject(err);
    }

    // 2) Forbidden (403) - can also indicate session issues
    if (status === 403 && !isAuthEndpointUrl(cfg?.url) && !cfg?.url?.includes("/signin")) {
      const isSessionCheckOnSignin =
        cfg?.url?.includes("frappe.auth.get_logged_user") &&
        typeof window !== "undefined" &&
        window.location.pathname.includes("signin");
      if (isSessionCheckOnSignin) return Promise.reject(err);

      // Check if error message indicates session/auth issue
      const errorMsg = responseData?.message || responseData?.exception || "";
      const isSessionError = typeof errorMsg === "string" && (
        errorMsg.toLowerCase().includes("session") ||
        errorMsg.toLowerCase().includes("expired") ||
        // Only treat unauthorized as session error if it's explicitly "not authorized"
        // NOT if it's "not permitted" or "permission error"
        errorMsg.toLowerCase().includes("not authorized") ||
        errorMsg.toLowerCase().includes("authentication") ||
        errorMsg.toLowerCase().includes("not authenticated") ||
        errorMsg.toLowerCase().includes("login required")
      );

      if (isSessionError) {
        handleSessionExpiry("Session expired (403 Forbidden - Authentication required)");
        return Promise.reject(err);
      }
    }

    // 3) CSRF Retry (only for non-session-expiry 403 errors)
    // Skip if we already handled session expiry above
    if ((status === 401 || status === 403) && cfg && !inFlightRetry.has(cfg) && !window.__isSigningOut) {
      // Only retry CSRF if it's not a session expiry issue
      const errorMsg = responseData?.message || responseData?.exception || "";
      const isSessionError = typeof errorMsg === "string" && (
        errorMsg.toLowerCase().includes("session") ||
        errorMsg.toLowerCase().includes("expired") ||
        errorMsg.toLowerCase().includes("unauthorized") ||
        errorMsg.toLowerCase().includes("authentication") ||
        errorMsg.toLowerCase().includes("not authenticated")
      );

      if (!isSessionError) {
        inFlightRetry.add(cfg);
        bootstrapCsrfFromStorage(api);
        // We don't bootstrap tokens anymore.
      }
    }
    else if ((status === 401 || status === 403) && hasAuth && !window.__isSigningOut) {
      // Log auth failures if auth token is present (unexpected) - sanitized for security
      if (isDevelopment) {
        const endpoint = cfg?.url ? (cfg.url.split('/api/method/')[1] || '[endpoint]') : '[unknown]';
        console.error(`❌ Auth Error (${status}):`, {
          endpoint: endpoint,
          hasAuthHeader: !!cfg?.headers?.Authorization,
          hasCookies: typeof document !== "undefined" && document.cookie.includes("sid"),
          method: cfg?.method,
          errorType: err?.response?.data?.exception || err?.response?.data?.message || '[error]',
        });
      }
    }

    // Normalize error (sanitize to remove URLs and translate to user-friendly)
    const data = err?.response?.data;
    
    // Use centralized error message utility for user-friendly messages
    const userFriendlyMsg = getUserFriendlyError(err, status);
    
    const norm = new Error(userFriendlyMsg);
    norm.status = status;
    // Don't expose full error data in production
    norm.data = isDevelopment ? data : { message: userFriendlyMsg };
    // Store original error for debugging (development only)
    if (isDevelopment) {
      norm.originalError = err;
    }
    throw norm;
  }
);

/* =========================
   PUBLIC HELPERS
   ========================= */

function ensureJsonHeaders(headers, payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
  const out = { ...(headers || {}) };
  if (!isFormData && payload && typeof payload === "object") {
    out["Content-Type"] = "application/json";
  }
  return out;
}

export async function apiGet(path, params, config = {}) {
  const url = buildUrl(path);
  const res = await api.get(url, { params, ...config });
  return res.data;
}

export function extractMessage(payload) {
  if (payload == null) return null;
  if (typeof payload !== "object") return payload;
  return payload.message ?? payload.data ?? payload;
}

export async function apiJSON(
  path,
  { method = "GET", params, data, body, headers } = {}
) {
  const url = buildUrl(path);
  const payload = data !== undefined ? data : body;
  const mergedHeaders = ensureJsonHeaders(headers, payload);

  // If Authorization is explicitly set to empty string, remove it from the request
  // This is needed for public endpoints like sign-up that don't require auth
  // Setting to null removes the header from axios defaults
  if (mergedHeaders.Authorization === "") {
    mergedHeaders.Authorization = null;
  }

  // Debug logging for shortlist API (development only, no URL exposure)
  if (isDevelopment && url.includes("shortlist_api.add_shortlisted_product")) {
    const endpoint = url.split('/api/method/')[1] || '[endpoint]';
    console.log("🔍 apiJSON - About to send request:", {
      endpoint: endpoint,
      method,
      hasMortgageProduct: !!payload?.mortgage_product,
      payloadKeys: payload ? Object.keys(payload) : []
    });
  }

  const res = await api.request({
    url,
    method,
    params,
    data: payload,
    headers: mergedHeaders,
  });
  return res.data;
}

export async function apiUpload(path, formData, extraHeaders = {}) {
  const url = buildUrl(path);
  // ✅ Don't set Content-Type manually - let the browser set it with the boundary
  // Setting it manually breaks FormData encoding, especially in production
  const res = await api.post(url, formData, {
    headers: { ...extraHeaders },
    // Let axios/browser automatically set Content-Type with boundary for FormData
  });
  return res.data;
}

export const apiPost = (p, d, h) => apiJSON(p, { method: "POST", data: d, headers: h });
export const apiPut = (p, d, h) => apiJSON(p, { method: "PUT", data: d, headers: h });
export const apiDelete = async (path, params, headers) => {
  const url = buildUrl(path);
  const res = await api.request({ url, method: "DELETE", params, headers });
  return res.data;
};

export { api };